import './membership-management.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';
import { Provider, useDispatch } from 'react-redux';

import type {
  CheckAuthenticationResponse,
  LegacyAuthModalState,
  LoginType,
} from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import { selectAccountSettings, selectAuthentication } from '../../../auth/store/auth.selectors';
import { checkAuthentication, loadAccountSettings } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { useTranslation } from '../../../i18n';
import type { AccountSettingsResponse } from '../../../models/common.model';
import type { AppDispatch } from '../../../store';
import { store } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import type { Membership, MembershipState } from '../../models/membership.model';
import {
  selectMemberships,
  selectMembershipsLoading,
  selectMembershipUpdating,
} from '../../store/membership.selectors';
import { loadAddressData, loadMemberships } from '../../store/membership.thunk';
import { MembershipBanner } from '../membership-banner/membership-banner.component';
import { MembershipList } from '../membership-list/membership-list.component';

export interface MembershipManagementProps {
  // Content filtering props
  membershipId?: string;
  showBlock?: string; // 'always' | 'has_access' | 'no_access'
  tierIds?: string[];

  // Display configuration props
  title?: string;
  titleSize?: string;
  description?: string;
  showLogin?: boolean;
  showProgramName?: boolean;
  showTierImage?: boolean;
  showTierName?: boolean;
  showTierDescription?: boolean;
  showPrice?: boolean;
  showQuickAdd?: boolean;
  maxWidth?: boolean;
  showBanner?: boolean;
  showBannerImage?: boolean;
  redirectToCurrentUrl?: boolean;

  // CSS custom property props
  columns?: string;
  columnAlignment?: string;
  sectionBackground?: string;
  columnBackground?: string;
  sectionTextColor?: string;
  columnTextColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  columnBorderColor?: string;
  columnBorderWidth?: string;
  columnBorderRadius?: string;
  buttonBorderRadius?: string;
  verticalPadding?: string;
}

const MembershipManagementComponent: FunctionComponent<MembershipManagementProps> = ({
  membershipId,
  showBlock,
  tierIds,
  title,
  titleSize = 'h2',
  description,
  showLogin = false,
  showProgramName = false,
  showTierImage = false,
  showTierName = false,
  showTierDescription = false,
  showPrice = false,
  showQuickAdd = false,
  maxWidth = false,
  showBanner = true,
  showBannerImage = true,
  redirectToCurrentUrl = false,
  columns,
  columnAlignment,
  sectionBackground,
  columnBackground,
  sectionTextColor,
  columnTextColor,
  buttonColor,
  buttonTextColor,
  columnBorderColor,
  columnBorderWidth,
  columnBorderRadius,
  buttonBorderRadius,
  verticalPadding,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const rootRef = useRef<HTMLDivElement>(null);

  // Redux selectors
  const memberships: Membership[] | undefined = useAppSelector(selectMemberships);
  const loadingState = useAppSelector(selectMembershipsLoading);
  const updatingState = useAppSelector(selectMembershipUpdating);
  const authenticationResponse: CheckAuthenticationResponse | undefined =
    useAppSelector(selectAuthentication);
  const accountSettingsResponse: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { t } = useTranslation();

  useEffect(() => {
    if (!memberships && !loadingState) {
      dispatch(loadMemberships());
      dispatch(loadAddressData());
    }
    if (!authenticationResponse) {
      dispatch(checkAuthentication({ loginTypes: ['CUSTOMER' as LoginType] }));
    }
    if (!accountSettingsResponse?.accountSettings) {
      dispatch(loadAccountSettings());
    }
  }, [dispatch]);

  // Combine loading states
  const loading = useMemo(() => loadingState || updatingState, [loadingState, updatingState]);

  // Filter memberships
  const filteredMemberships = useMemo(() => {
    if (!memberships) return [];

    return (
      memberships
        // Filter by membershipId
        .filter(
          (membership) => !membershipId || membershipId === '' || membershipId === membership.id
        )
        // Filter by showBlock condition
        .filter((membership) => {
          if (!showBlock || showBlock === '' || showBlock === 'always') {
            return true;
          }
          if (showBlock === 'has_access' && membership.contractId && membership.contractId !== '') {
            return true;
          }
          return (
            showBlock === 'no_access' && (!membership.contractId || membership.contractId === '')
          );
        })
        // Filter tiers and remove memberships with no tiers
        .map((membership) => {
          if (tierIds && tierIds.length > 0) {
            return {
              ...membership,
              tiers: membership.tiers.filter((tier) => tierIds.includes(tier.id)),
            };
          }
          return membership;
        })
        .filter((membership) => membership.tiers && membership.tiers.length > 0)
    );
  }, [memberships, membershipId, showBlock, tierIds]);

  // Extract accountSettings from first membership
  const extractedAccountSettings = useMemo(() => {
    return filteredMemberships
      ?.map((membership) => membership?.accountSettings)
      .find((settings) => !!settings);
  }, [filteredMemberships]);

  // Calculate loginRedirectUrl
  const loginRedirectUrl = useMemo(() => {
    return filteredMemberships
      ?.map((membership) => {
        const tierUrl = membership?.tiers
          ?.map((tier) => tier?.postPurchaseUrl)
          .find((url) => !!url);
        return tierUrl ?? membership?.accountSettings?.defaultLandingUrl;
      })
      .find((url) => !!url);
  }, [filteredMemberships]);

  // Check if any active memberships
  const anyActiveMemberships = useMemo(() => {
    return (
      filteredMemberships &&
      filteredMemberships.length > 0 &&
      filteredMemberships.some((m) => m.state === ('PUBLISHED' as MembershipState))
    );
  }, [filteredMemberships]);

  // Check if all memberships have no contract
  const noMembershipsWithContract = useMemo(() => {
    return filteredMemberships?.every((m) => !m.contractId);
  }, [filteredMemberships]);

  // Build CSS custom properties style object
  const customStyles = useMemo(() => {
    const styleMap: Record<string, string> = {};
    if (columns) styleMap['--columns'] = columns;
    if (columnAlignment) styleMap['--columnAlignment'] = columnAlignment;
    if (sectionBackground) styleMap['--sectionBackground'] = sectionBackground;
    if (columnBackground) styleMap['--columnBackground'] = columnBackground;
    if (sectionTextColor) styleMap['--sectionTextColor'] = sectionTextColor;
    if (columnTextColor) styleMap['--columnTextColor'] = columnTextColor;
    if (buttonColor) styleMap['--buttonColor'] = buttonColor;
    if (buttonTextColor) styleMap['--buttonTextColor'] = buttonTextColor;
    if (columnBorderColor) styleMap['--columnBorderColor'] = columnBorderColor;
    if (columnBorderWidth) styleMap['--columnBorderWidth'] = columnBorderWidth;
    if (columnBorderRadius) styleMap['--columnBorderRadius'] = columnBorderRadius;
    if (buttonBorderRadius) styleMap['--buttonBorderRadius'] = buttonBorderRadius;
    if (verticalPadding) styleMap['--verticalPadding'] = verticalPadding;
    return styleMap;
  }, [
    columns,
    columnAlignment,
    sectionBackground,
    columnBackground,
    sectionTextColor,
    columnTextColor,
    buttonColor,
    buttonTextColor,
    columnBorderColor,
    columnBorderWidth,
    columnBorderRadius,
    buttonBorderRadius,
    verticalPadding,
  ]);

  // Set up CSS variables from accountSettings
  useEffect(() => {
    if (!rootRef.current || !extractedAccountSettings) return;
    const element = rootRef.current;
    if (extractedAccountSettings.cardBackgroundColor) {
      element.style.setProperty(
        '--single-settings-bg-color',
        extractedAccountSettings.cardBackgroundColor
      );
    }
    if (extractedAccountSettings.cardBorderColor) {
      element.style.setProperty(
        '--single-settings-brdr-color',
        extractedAccountSettings.cardBorderColor
      );
    }
    if (extractedAccountSettings.cardTextColor) {
      element.style.setProperty(
        '--single-settings-text-color',
        extractedAccountSettings.cardTextColor
      );
    }
    if (extractedAccountSettings.cardButtonBackgroundColor) {
      element.style.setProperty(
        '--single-settings-button-bg-color',
        extractedAccountSettings.cardButtonBackgroundColor
      );
    }
    if (extractedAccountSettings.cardButtonTextColor) {
      element.style.setProperty(
        '--single-settings-button-text-color',
        extractedAccountSettings.cardButtonTextColor
      );
    }
  }, [extractedAccountSettings]);

  // Memoized event handler
  const handleOpenLoginModal = useCallback(() => {
    authenticationFlowService.initiateAuthentication({
      state: 'LOGIN' as LegacyAuthModalState,
      allowedStates: ['LOGIN' as LegacyAuthModalState, 'RESET_PASSWORD' as LegacyAuthModalState],
      returnToUrl: loginRedirectUrl ?? `${window.location.origin}/apps/single/membership`,
    });
  }, [loginRedirectUrl]);

  return (
    <div ref={rootRef} className="membership-management" style={customStyles}>
      {/* Loading Spinner */}
      {loading && (
        <section
          className="single-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '16 / 9',
            maxHeight: '75vh',
            width: '100%',
          }}
        >
          <SpinnerComponent />
        </section>
      )}
      {/* Empty State */}
      {!loading && memberships && filteredMemberships.length === 0 && (
        <div
          className={`manage-membership-wrapper ${
            maxWidth ? 'page-width' : ''
          } ${authenticationResponse?.authenticated ? 'single-mb-1' : ''}`}
        >
          <div className="manage-membership-header">
            <h2 className={`single-product-title ${titleSize}`}>
              <span>{t('membership.noActiveMemberships')}</span>
            </h2>
          </div>
        </div>
      )}
      {/* Banner */}
      {!loading && filteredMemberships.length > 0 && showBanner && (
        <MembershipBanner
          accountSettings={accountSettingsResponse.accountSettings}
          header={title}
          headerSize={titleSize}
          subheader={description}
          showBanner={showBanner}
          showBannerImage={showBannerImage}
          showHeader={anyActiveMemberships && noMembershipsWithContract}
          showSubheader={anyActiveMemberships && noMembershipsWithContract}
        />
      )}
      {/* Membership List */}
      {!loading && filteredMemberships.length > 0 && (
        <div
          className={`manage-membership-wrapper ${
            maxWidth ? 'page-width' : ''
          } ${authenticationResponse?.authenticated ? 'single-mb-1' : ''}`}
        >
          <MembershipList
            memberships={filteredMemberships}
            accountSettings={accountSettingsResponse.accountSettings}
            showProgramName={showProgramName}
            showTierImage={showTierImage}
            showTierName={showTierName}
            showTierDescription={showTierDescription}
            showPrice={showPrice}
            showQuickAdd={showQuickAdd}
            redirectToCurrentUrl={redirectToCurrentUrl}
          />
          {/* Login Prompt */}
          {showLogin &&
            (authenticationResponse?.loginTypes?.length ?? 0) > 0 &&
            loginRedirectUrl &&
            loginRedirectUrl !== '' && (
              <div className="membership-login">
                <h3 className="single-login-header h3 single-h3">
                  {accountSettingsResponse?.accountSettings.alreadyMemberText ||
                    t('common.alreadyMember')}
                  &nbsp;
                  <span onClick={handleOpenLoginModal}>{t('auth.logIn')}</span>
                </h3>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipManagement = memo(MembershipManagementComponent);

// Wrapper with Redux Provider for standalone widget usage
export const MembershipManagementWithStore: FunctionComponent<MembershipManagementProps> = (
  props
) => {
  return (
    <Provider store={store}>
      <MembershipManagement {...props} />
    </Provider>
  );
};
